/**
 * Builds the figures on an account statement, one section per currency.
 *
 * A user holds at most one wallet per currency (wallet-ms enforces it), so a
 * currency names exactly one wallet. Every figure here is relative to that
 * wallet: a row is a debit when the wallet sent the money and a credit when it
 * received it, and only posted (Completed) transactions move the balance.
 *
 * There is no stored balance-as-of, so the opening balance is derived:
 *
 *   opening = balance now - net movement from the period start until now
 *
 * which needs every posted transaction from the start date to today, not just
 * to the end date. The rows after the end date are used for that derivation
 * and then dropped from the listing. By construction the statement then
 * reconciles -- opening + credits - debits - charges = closing -- and a
 * statement ending today closes on the balance shown in the app.
 *
 * Money is summed in integer minor units so a long statement does not drift
 * by a cent through float addition.
 */

export type StatementTxn = {
  id?: string | number;
  reference?: string;
  description?: string;
  amount?: string | number;
  charges?: string | number;
  currency_code?: string;
  status?: string;
  created_at?: string;
  sender_wallet_number?: string | number;
  receiver_wallet_number?: string | number;
};

export type StatementRow = {
  date: string;
  description: string;
  reference: string;
  debit: number;
  credit: number;
  charges: number;
  balance: number;
};

export type StatementSection = {
  currencyCode: string;
  walletNumber: string;
  currentBalance: number;
  openingBalance: number;
  closingBalance: number;
  totalDebit: number;
  totalCredit: number;
  totalCharges: number;
  rows: StatementRow[];
  /** Rows in the period that were not posted, so a reader knows they were left out on purpose. */
  unpostedCount: number;
};

const toCents = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};
const fromCents = (cents: number): number => cents / 100;

/**
 * "Ghana Cedi (GHS)" -> "GHS"; "GHS" -> "GHS". The wallet service renders a
 * currency as its name with the code in brackets; transactions carry the bare
 * code, and the two have to be compared.
 */
export function extractCurrencyCode(label?: string | null): string {
  const text = String(label ?? "").trim();
  const bracketed = text.match(/\(([A-Za-z]{3,4})\)\s*$/);
  if (bracketed) return bracketed[1].toUpperCase();
  if (text.length <= 4) return text.toUpperCase();
  return text.toUpperCase();
}

export const isPosted = (txn: StatementTxn): boolean =>
  String(txn.status ?? "").toLowerCase() === "completed";

/** Movement of one transaction relative to one wallet, in minor units. */
export function movementFor(
  txn: StatementTxn,
  walletNumber: string,
): { debit: number; credit: number; charges: number } | null {
  const wallet = String(walletNumber);
  const sender = String(txn.sender_wallet_number ?? "");
  const receiver = String(txn.receiver_wallet_number ?? "");
  const amount = toCents(txn.amount);

  if (sender === wallet) {
    // The sender bears the charge; the receiver gets the face amount.
    return { debit: amount, credit: 0, charges: toCents(txn.charges) };
  }
  if (receiver === wallet) {
    return { debit: 0, credit: amount, charges: 0 };
  }
  return null;
}

const startOfDay = (isoDate: string) => new Date(`${isoDate}T00:00:00`);
const endOfDay = (isoDate: string) => new Date(`${isoDate}T23:59:59.999`);

export function buildSection(input: {
  currencyCode: string;
  walletNumber: string;
  currentBalance: string | number;
  /** Every transaction for this wallet from startDate up to now. */
  transactions: StatementTxn[];
  startDate: string;
  endDate: string;
}): StatementSection {
  const { currencyCode, walletNumber, startDate, endDate } = input;
  const from = startOfDay(startDate).getTime();
  const to = endOfDay(endDate).getTime();
  const currentCents = toCents(input.currentBalance);

  type Dated = { txn: StatementTxn; at: number; move: NonNullable<ReturnType<typeof movementFor>> };
  const posted: Dated[] = [];
  let unpostedCount = 0;

  for (const txn of input.transactions) {
    const at = new Date(txn.created_at ?? "").getTime();
    if (Number.isNaN(at) || at < from) continue;
    const move = movementFor(txn, walletNumber);
    if (!move) continue;
    if (!isPosted(txn)) {
      if (at <= to) unpostedCount += 1;
      continue;
    }
    posted.push({ txn, at, move });
  }
  posted.sort((a, b) => a.at - b.at);

  // Everything posted since the start date, whether inside the period or
  // after it, stands between the opening balance and the balance now.
  let netSinceStart = 0;
  for (const { move } of posted) {
    netSinceStart += move.credit - move.debit - move.charges;
  }
  const openingCents = currentCents - netSinceStart;

  let running = openingCents;
  let totalDebit = 0;
  let totalCredit = 0;
  let totalCharges = 0;
  const rows: StatementRow[] = [];

  for (const { txn, at, move } of posted) {
    if (at > to) break;
    running += move.credit - move.debit - move.charges;
    totalDebit += move.debit;
    totalCredit += move.credit;
    totalCharges += move.charges;
    rows.push({
      date: new Date(at).toISOString(),
      description: String(txn.description ?? "").trim() || "Transaction",
      reference: String(txn.reference ?? ""),
      debit: fromCents(move.debit),
      credit: fromCents(move.credit),
      charges: fromCents(move.charges),
      balance: fromCents(running),
    });
  }

  return {
    currencyCode,
    walletNumber: String(walletNumber),
    currentBalance: fromCents(currentCents),
    openingBalance: fromCents(openingCents),
    closingBalance: fromCents(running),
    totalDebit: fromCents(totalDebit),
    totalCredit: fromCents(totalCredit),
    totalCharges: fromCents(totalCharges),
    rows,
    unpostedCount,
  };
}

export const formatMoney = (value: number): string =>
  value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
